import { useState, useEffect } from 'react';
import { Card, Col, Row, Form, Alert, Badge } from 'react-bootstrap';
import {
  faUser,
  faEnvelope,
  faBuilding,
  faBriefcase,
  faCalendar,
  faShieldAlt,
  faEdit,
  faKey,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from 'store';
import {
  fetchCurrentUser,
  updateCurrentUser,
  fetchUsers,
  UserType
} from 'store/userSlice';
import axiosClient from 'api/axiosClient';
import { changePassword, ChangePasswordPayload } from 'api/authApi';
import PhoenixLoader from 'components/common/PhoenixLoader';

interface PasswordRequirements {
  hasMinLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  strength: number;
}

const validatePassword = (password: string): PasswordRequirements => {
  const requirements = {
    hasMinLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
  };

  let strength = 0;
  const checks = Object.values(requirements);
  const passedChecks = checks.filter(Boolean).length;
  strength = (passedChecks / checks.length) * 100;

  return {
    ...requirements,
    strength
  };
};

const getStrengthInfo = (strength: number) => {
  if (strength < 40)
    return { label: 'Weak', color: 'danger', variant: 'danger' };
  if (strength < 70)
    return { label: 'Fair', color: 'warning', variant: 'warning' };
  if (strength < 90) return { label: 'Good', color: 'info', variant: 'info' };
  return { label: 'Strong', color: 'success', variant: 'success' };
};

const Profile = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser, loading, error, data } = useSelector(
    (state: RootState) => state.users
  );
  const authUser = useSelector((state: RootState) => state.auth);

  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordRequirements, setPasswordRequirements] =
    useState<PasswordRequirements>({
      hasMinLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecialChar: false,
      strength: 0
    });

  // Add this state to store user data with names
  const [userWithNames, setUserWithNames] = useState<UserType | null>(null);
  const [loadingNames, setLoadingNames] = useState(false);

  useEffect(() => {
    dispatch(fetchCurrentUser());
    // Also fetch users list to get names
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    if (currentUser) {
      setFormData({
        name: currentUser.name || '',
        email: currentUser.email || ''
      });

      // Try to get user with company/department names
      findUserWithNames();
    }
  }, [currentUser]);

  useEffect(() => {
    if (passwordData.new_password) {
      setPasswordRequirements(validatePassword(passwordData.new_password));
    } else {
      setPasswordRequirements({
        hasMinLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecialChar: false,
        strength: 0
      });
    }
  }, [passwordData.new_password]);

  // Function to find user with proper names
  const findUserWithNames = async () => {
    if (!currentUser) return;

    setLoadingNames(true);
    try {
      // OPTION 1: Try to find in the users list (from UserTable)
      if (data && data.length > 0) {
        const userFromList = data.find(
          (user: UserType) => user.id === currentUser.id
        );
        if (
          userFromList &&
          (userFromList.company_name || userFromList.department_name)
        ) {
          setUserWithNames(userFromList);
          setLoadingNames(false);
          return;
        }
      }

      // OPTION 2: If not found in list, fetch from /users/ endpoint directly
      const response = await axiosClient.get('/users/', {
        params: { skip: 0, limit: 1000 }
      });

      const foundUser = response.data.find(
        (user: UserType) => user.id === currentUser.id
      );
      if (foundUser) {
        setUserWithNames(foundUser);
      } else {
        // If still not found, use currentUser as is
        setUserWithNames(currentUser);
      }
    } catch (error) {
      console.error('Error finding user with names:', error);
      setUserWithNames(currentUser);
    } finally {
      setLoadingNames(false);
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    setSuccessMessage('');
    setErrorMessage('');
    if (isEditing && currentUser) {
      setFormData({
        name: currentUser.name || '',
        email: currentUser.email || ''
      });
    }
  };

  const handlePasswordToggle = () => {
    setIsChangingPassword(!isChangingPassword);
    setPasswordData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);

    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    try {
      await dispatch(
        updateCurrentUser({
          name: formData.name,
          email: formData.email
        })
      ).unwrap();

      setSuccessMessage('Profile updated successfully!');
      setIsEditing(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: unknown) {
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to update profile'
      );
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setErrorMessage('New passwords do not match');
      return;
    }

    const requirements = validatePassword(passwordData.new_password);
    const allRequirementsMet =
      requirements.hasMinLength &&
      requirements.hasUppercase &&
      requirements.hasLowercase &&
      requirements.hasNumber &&
      requirements.hasSpecialChar;

    if (!allRequirementsMet) {
      setErrorMessage('New password does not meet all requirements');
      return;
    }

    if (!passwordData.current_password.trim()) {
      setErrorMessage('Current password is required');
      return;
    }

    if (!authUser.access_token) {
      setErrorMessage('Not authenticated. Please log in again.');
      return;
    }

    try {
      const payload: ChangePasswordPayload = {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
        confirm_password: passwordData.confirm_password
      };

      await changePassword(payload, authUser.access_token);

      setSuccessMessage('Password changed successfully!');
      setIsChangingPassword(false);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: unknown) {
      const axiosError = err as {
        response?: {
          data?: { detail?: string; message?: string };
        };
        message?: string;
      };

      const errorMessage =
        axiosError.response?.data?.detail ||
        axiosError.response?.data?.message ||
        axiosError.message ||
        'Failed to change password. Please try again.';

      setErrorMessage(errorMessage);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'super_user':
        return 'danger';
      case 'company_admin':
        return 'warning';
      case 'manager':
        return 'info';
      default:
        return 'secondary';
    }
  };

  // Determine which user data to display
  const displayUser = userWithNames || currentUser;

  if (loading && !currentUser) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <PhoenixLoader />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="mb-4">My Profile</h2>

        {successMessage && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSuccessMessage('')}
          >
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setErrorMessage('')}
          >
            {errorMessage}
          </Alert>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="g-3">
          <Col xs={12} lg={8}>
            <Card className="h-100">
              <Card.Header className="d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Personal Information</h5>
                <Button
                  variant={isEditing ? 'secondary' : 'primary'}
                  size="sm"
                  onClick={handleEditToggle}
                  disabled={loading}
                >
                  <FontAwesomeIcon
                    icon={isEditing ? faUser : faEdit}
                    className="me-2"
                  />
                  {isEditing ? 'Cancel' : 'Edit Profile'}
                </Button>
              </Card.Header>
              <Card.Body>
                {isEditing ? (
                  <Form onSubmit={handleUpdateProfile}>
                    <Row className="g-3">
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>
                            <FontAwesomeIcon
                              icon={faUser}
                              className="me-2 text-body-tertiary"
                            />
                            Full Name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter your full name"
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>
                            <FontAwesomeIcon
                              icon={faEnvelope}
                              className="me-2 text-body-tertiary"
                            />
                            Email Address
                          </Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter your email"
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <div className="d-flex gap-2">
                          <Button
                            variant="primary"
                            type="submit"
                            disabled={loading}
                          >
                            {loading ? 'Saving...' : 'Save Changes'}
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={handleEditToggle}
                            disabled={loading}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    <div className="d-flex align-items-center">
                      <div className="icon-circle bg-primary-subtle text-primary me-3">
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                      <div>
                        <p className="mb-0 text-body-tertiary small">
                          Full Name
                        </p>
                        <h6 className="mb-0">
                          {displayUser?.name || authUser.name || 'N/A'}
                        </h6>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="icon-circle bg-success-subtle text-success me-3">
                        <FontAwesomeIcon icon={faEnvelope} />
                      </div>
                      <div>
                        <p className="mb-0 text-body-tertiary small">
                          Email Address
                        </p>
                        <h6 className="mb-0">
                          {displayUser?.email || authUser.email || 'N/A'}
                        </h6>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="icon-circle bg-info-subtle text-info me-3">
                        <FontAwesomeIcon icon={faBuilding} />
                      </div>
                      <div>
                        <p className="mb-0 text-body-tertiary small">Company</p>
                        <h6 className="mb-0">
                          {loadingNames ? (
                            <span className="spinner-border spinner-border-sm me-2" />
                          ) : (
                            displayUser?.company_name || 'N/A'
                          )}
                        </h6>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="icon-circle bg-warning-subtle text-warning me-3">
                        <FontAwesomeIcon icon={faBriefcase} />
                      </div>
                      <div>
                        <p className="mb-0 text-body-tertiary small">
                          Department
                        </p>
                        <h6 className="mb-0">
                          {loadingNames ? (
                            <span className="spinner-border spinner-border-sm me-2" />
                          ) : (
                            displayUser?.department_name || 'Not assigned'
                          )}
                        </h6>
                      </div>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} lg={4}>
            <Card className="h-100">
              <Card.Header>
                <h5 className="mb-0">Account Details</h5>
              </Card.Header>
              <Card.Body>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <p className="mb-1 text-body-tertiary small">
                      <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                      Role
                    </p>
                    <Badge
                      bg={getRoleBadgeVariant(
                        displayUser?.role_name || authUser.role || ''
                      )}
                      className="px-3 py-2"
                    >
                      {displayUser?.role_name || authUser.role || 'N/A'}
                    </Badge>
                  </div>
                  <div>
                    <p className="mb-1 text-body-tertiary small">
                      <FontAwesomeIcon icon={faCalendar} className="me-2" />
                      Account Status
                    </p>
                    <Badge
                      bg={displayUser?.is_active ? 'success' : 'danger'}
                      className="px-3 py-2"
                    >
                      {displayUser?.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div>
                    <p className="mb-1 text-body-tertiary small">
                      <FontAwesomeIcon icon={faCalendar} className="me-2" />
                      Member Since
                    </p>
                    <p className="mb-0 fw-semibold">
                      {displayUser?.created_at
                        ? formatDate(displayUser.created_at)
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12}>
            <Card>
              <Card.Header className="d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Security</h5>
                <Button
                  variant={isChangingPassword ? 'secondary' : 'outline-primary'}
                  size="sm"
                  onClick={handlePasswordToggle}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faKey} className="me-2" />
                  {isChangingPassword ? 'Cancel' : 'Change Password'}
                </Button>
              </Card.Header>
              {isChangingPassword && (
                <Card.Body>
                  <Form onSubmit={handleChangePassword}>
                    <Row className="g-3">
                      <Col xs={12} md={4}>
                        <Form.Group>
                          <Form.Label>Old Password</Form.Label>
                          <div className="position-relative">
                            <Form.Control
                              type={showCurrentPassword ? 'text' : 'password'}
                              name="current_password"
                              value={passwordData.current_password}
                              onChange={handlePasswordChange}
                              required
                              placeholder="Enter old password"
                              style={{ paddingRight: '2.5rem' }}
                            />
                            <FontAwesomeIcon
                              icon={showCurrentPassword ? faEyeSlash : faEye}
                              className="text-body-tertiary fs-9 position-absolute"
                              style={{
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                cursor: 'pointer',
                                zIndex: 10
                              }}
                              onClick={() =>
                                setShowCurrentPassword(!showCurrentPassword)
                              }
                            />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Group>
                          <Form.Label>New Password</Form.Label>
                          <div className="position-relative">
                            <Form.Control
                              type={showNewPassword ? 'text' : 'password'}
                              name="new_password"
                              value={passwordData.new_password}
                              onChange={handlePasswordChange}
                              required
                              placeholder="Enter new password"
                              style={{ paddingRight: '2.5rem' }}
                            />
                            <FontAwesomeIcon
                              icon={showNewPassword ? faEyeSlash : faEye}
                              className="text-body-tertiary fs-9 position-absolute"
                              style={{
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                cursor: 'pointer',
                                zIndex: 10
                              }}
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                            />
                          </div>

                          {/* Password requirements display */}
                          {passwordData.new_password && (
                            <div className="mt-2">
                              <div className="d-flex flex-column gap-1 small">
                                <div className="d-flex align-items-center">
                                  <span
                                    className={`me-2 ${
                                      passwordRequirements.hasUppercase
                                        ? 'text-success'
                                        : 'text-danger'
                                    }`}
                                  >
                                    {passwordRequirements.hasUppercase
                                      ? '✓'
                                      : '✗'}
                                  </span>
                                  <span>
                                    At least one uppercase letter (A-Z)
                                  </span>
                                </div>
                                <div className="d-flex align-items-center">
                                  <span
                                    className={`me-2 ${
                                      passwordRequirements.hasLowercase
                                        ? 'text-success'
                                        : 'text-danger'
                                    }`}
                                  >
                                    {passwordRequirements.hasLowercase
                                      ? '✓'
                                      : '✗'}
                                  </span>
                                  <span>
                                    At least one lowercase letter (a-z)
                                  </span>
                                </div>
                                <div className="d-flex align-items-center">
                                  <span
                                    className={`me-2 ${
                                      passwordRequirements.hasNumber
                                        ? 'text-success'
                                        : 'text-danger'
                                    }`}
                                  >
                                    {passwordRequirements.hasNumber ? '✓' : '✗'}
                                  </span>
                                  <span>At least one number (0-9)</span>
                                </div>
                                <div className="d-flex align-items-center">
                                  <span
                                    className={`me-2 ${
                                      passwordRequirements.hasSpecialChar
                                        ? 'text-success'
                                        : 'text-danger'
                                    }`}
                                  >
                                    {passwordRequirements.hasSpecialChar
                                      ? '✓'
                                      : '✗'}
                                  </span>
                                  <span>
                                    At least one special character (!@#$%^&*
                                    etc.)
                                  </span>
                                </div>
                                <div className="d-flex align-items-center">
                                  <span
                                    className={`me-2 ${
                                      passwordRequirements.hasMinLength
                                        ? 'text-success'
                                        : 'text-danger'
                                    }`}
                                  >
                                    {passwordRequirements.hasMinLength
                                      ? '✓'
                                      : '✗'}
                                  </span>
                                  <span>Minimum 8 characters</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Group>
                          <Form.Label>Confirm New Password</Form.Label>
                          <div className="position-relative">
                            <Form.Control
                              type={showConfirmPassword ? 'text' : 'password'}
                              name="confirm_password"
                              value={passwordData.confirm_password}
                              onChange={handlePasswordChange}
                              required
                              placeholder="Confirm new password"
                              style={{ paddingRight: '2.5rem' }}
                            />
                            <FontAwesomeIcon
                              icon={showConfirmPassword ? faEyeSlash : faEye}
                              className="text-body-tertiary fs-9 position-absolute"
                              style={{
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                cursor: 'pointer',
                                zIndex: 10
                              }}
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                            />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <div className="d-flex gap-2">
                          <Button
                            variant="primary"
                            type="submit"
                            disabled={loading}
                          >
                            Update Password
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={handlePasswordToggle}
                            disabled={loading}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              )}
            </Card>
          </Col>
        </Row>
      </div>

      <style>{`
        .icon-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
        }
      `}</style>
    </div>
  );
};

export default Profile;
